import axios from './axios-instance';
import ApiResponse from "../models/ApiResponse";
import Client from "../models/Client";
import ClientDTO from "../models/ClientDTO";
import DashboardTotals from "../models/DashboardTotals";
import ClientSearchParams from "../models/ClientSearchParams";
import {getDateFromString} from "../utils/DateTimeUtils";
import axiosInstance from "./axios-instance";

export const getTotalActiveClients = async (userId: number): Promise<ApiResponse<number>> => {
    try {
        const response = await axios.get('/client/total/' + userId);
        return {
            data: response.data,
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get total clients', err.response?.data || err.message)
        return {
            data:  0,
            status: err.status
        };
    }
}

export const createNewClient = async(clientForm: ClientDTO): Promise<ApiResponse<Client>> => {
    try {
        const response = await axios.post('/client/add', clientForm);
        return {
            data: mapResponseToClient(response.data),
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to add new client:', err.response?.data || err.message)
        return {
            data:  null,
            status: err.status
        };
    }
};

export const getRecentClients = async(userId: number): Promise<ApiResponse<Client[]>> => {
    try {
        const response = await axios.get('/client/get-recent/' + userId);
        return {
            data: response.data.map((client: any) => (mapResponseToClient(client))),
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get clients: ', err.response?.data || err.message);
        return {
            data:  null,
            status: err.status
        };
    }
};

export const getDashboardTotals = async (userId: number): Promise<ApiResponse<DashboardTotals>> => {
    const defaultTotals: DashboardTotals = {
        clients: 0,
        followUps: 0,
        leads: 0
    }
    try {
        const response = await axios.get('/client/totals/' + userId);
        return {
            data: response.data ? response.data as DashboardTotals : defaultTotals,
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get dashboard totals: ', err.response?.data || err.message);
        return {
            data: defaultTotals,
            status: err.status
        }
    }
};

export const getClientList = async (params: ClientSearchParams): Promise<ApiResponse<{ clients: Client[], totalCount: number }>> => {
    try {
        const response = await axios.post('/client/page', params);
        const clientsList = response.data.content.map((client: any) => (mapResponseToClient(client)));
        return {
            data: {
                clients: clientsList as Client[],
                totalCount: response.data.totalElements
            },
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get clients page: ', err.response?.data || err.message);
        return {
            data: {
                clients: [],
                totalCount: 0
            },
            status: err.status
        }
    }
};

export const getClientById = async (id: string | number): Promise<ApiResponse<Client>> => {
    try {
        const response = await axios.get('/client/id/' + id);
        const client = response.data;
        if (client) {
            return {
                data: mapResponseToClient(response.data),
                status: response.status
            }
        }
        return {
            data: null,
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to get client by id', err.response?.data || err.message);
        return {
            data: null,
            status: err.status
        };
    }
};

export const updateClient = async (client: Client): Promise<ApiResponse<Client>> => {
    try {
        const response = await axiosInstance.post('/client/update', client);
        return {
            data: mapResponseToClient(response.data),
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to update client', err.response?.data || err.message);
        return {
            data: null,
            status: err.status
        };
    }
};

export const deleteClient = async (clientId: number): Promise<{status: number}> => {
    try {
        const response = await axiosInstance.delete('/client/delete/' + clientId);
        return {status: response.status};
    } catch (err: any) {
        console.error('Failed to delete client', err.response?.data || err.message);
        return {status: err.status}
    }
};

const mapResponseToClient = (clientData: any): Client => {
    return {
        ...clientData,
        lastActivityDate: clientData.lastActivityDate ? getDateFromString(clientData.lastActivityDate) : null,
        created: clientData.created ? getDateFromString(clientData.created) : null
    }
}